import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield,
  faUsers,
  faChartLine,
  faGears,
  faShieldHalved,
  faHeadset,
  faGraduationCap,
  faHeartPulse,
  faBuilding,
  faPlane,
  faMoneyBillWave,
  faIndustry,
  faBriefcase,
  faUserTie,
  faBars,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FaWhatsapp } from 'react-icons/fa';
import SiteFooter from '../components/SiteFooter';
import AppLogo from '../components/AppLogo';
import { SALES_PHONE, SALES_EMAIL, WHATSAPP_URL } from '../lib/siteContact';
import './landing.css';

const whyItems = [
  {
    icon: faUsers,
    title: 'Reach More Customers',
    text: 'Connect with billions of WhatsApp users worldwide.',
  },
  {
    icon: faChartLine,
    title: 'High Open & Response Rates',
    text: 'WhatsApp messages get higher engagement than SMS & Email.',
  },
  {
    icon: faGears,
    title: 'Automated Communication',
    text: 'Send alerts, updates, reminders & notifications automatically.',
  },
  {
    icon: faShieldHalved,
    title: 'Build Customer Trust',
    text: 'Official business profile with verified green tick builds trust.',
  },
  {
    icon: faHeadset,
    title: 'Better Customer Support',
    text: 'Manage chats, automate responses and provide faster support.',
  },
];

const industries = [
  { icon: faGraduationCap, label: 'Education' },
  { icon: faHeartPulse, label: 'Healthcare' },
  { icon: faBuilding, label: 'Real Estate' },
  { icon: faPlane, label: 'Travel & Hospitality' },
  { icon: faMoneyBillWave, label: 'Finance' },
  { icon: faIndustry, label: 'Manufacturing' },
];

const Landing = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 992) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="landing">
      <header className="landing-header">
        <div className="landing-container landing-header__inner">
          <AppLogo to="/" variant="header" onClick={closeMenu} />
          <button
            type="button"
            className="landing-header__menu-btn"
            aria-expanded={menuOpen}
            aria-controls="landing-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
          <nav id="landing-nav" className={`landing-header__nav${menuOpen ? ' landing-header__nav--open' : ''}`}>
            <a href="#about" onClick={closeMenu}>About</a>
            <a href="#why" onClick={closeMenu}>Why WhatsApp</a>
            <a href="#industries" onClick={closeMenu}>Industries</a>
            <a href="#contact" onClick={closeMenu}>Contact</a>
            <Link to="/login" className="landing-header__login" onClick={closeMenu}>
              Login
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-container landing-hero__grid">
            <div className="landing-hero__content">
              <div className="landing-meta-badge">
                <img
                  src={`${process.env.PUBLIC_URL}/meta.jpeg`}
                  alt="Meta Business Partner"
                  className="landing-meta-badge__img"
                />
              </div>
              <h1>
                Powering Business Conversations on <span>WhatsApp</span>
              </h1>
              <p>
                Official Meta Business Partner helping businesses engage customers, automate
                communication and grow with WhatsApp Business API.
              </p>
              <div className="landing-hero__actions">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="landing-btn landing-btn--whatsapp">
                  <FaWhatsapp aria-hidden />
                  Chat on WhatsApp
                </a>
                <a href={`mailto:${SALES_EMAIL}`} className="landing-btn landing-btn--outline">
                  Contact Sales
                </a>
                <a href={`mailto:${SALES_EMAIL}?subject=Demo%20Request`} className="landing-btn landing-btn--outline">
                  Request Demo
                </a>
              </div>
            </div>

            <div className="landing-hero__visual">
              <img
                src={`${process.env.PUBLIC_URL}/mobile.png`}
                alt="WhatsApp Business conversation on mobile"
                className="landing-phone-img"
              />
            </div>
          </div>
        </section>

        <section id="about" className="landing-about">
          <div className="landing-container">
            <h2>About Us</h2>
            <p>
              Innovative Info Solutions is a technology provider helping businesses leverage the
              WhatsApp Business API to communicate with customers efficiently. We provide end-to-end
              WhatsApp Business solutions including API integration, automation, chatbot setup, and
              customer engagement tools.
            </p>
            <div className="landing-about__features">
              <div className="landing-about__feature">
                <FontAwesomeIcon icon={faUserShield} />
                <span>Meta Business Partner</span>
              </div>
              <div className="landing-about__feature">
                <FontAwesomeIcon icon={faShieldHalved} />
                <span>Secure &amp; Reliable Platform</span>
              </div>
              <div className="landing-about__feature">
                <FontAwesomeIcon icon={faUserTie} />
                <span>Expert Support Team</span>
              </div>
              <div className="landing-about__feature">
                <FontAwesomeIcon icon={faBriefcase} />
                <span>Custom Business Solutions</span>
              </div>
            </div>
          </div>
        </section>

        <section id="why" className="landing-why">
          <div className="landing-container">
            <h2>Why Choose WhatsApp Business API?</h2>
            <div className="landing-why__grid">
              {whyItems.map((item) => (
                <article key={item.title} className="landing-why__card">
                  <div className="landing-why__icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="industries" className="landing-industries">
          <div className="landing-container">
            <h2>Industries We Serve</h2>
            <div className="landing-industries__grid">
              {industries.map((item) => (
                <div key={item.label} className="landing-industries__item">
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter id="contact" />
    </div>
  );
};

export default Landing;
